import type {
  Guest,
  SeatingRoomConfig,
  SeatingState,
  SeatingTable,
  SeatingTestGuest,
  SeatingTestGuestDraft,
} from '@/lib/types'

export const UNASSIGNED_ZONE_ID = 'unassigned'

/** Default hall dimensions and table diameter (in centimeters). */
export const DEFAULT_ROOM_CONFIG: SeatingRoomConfig = {
  roomWidth: 1000,
  roomHeight: 700,
  tableDiameter: 150,
}

/** Bounds for the configurable hall dimensions and table diameter (centimeters). */
export const ROOM_LIMITS = {
  minRoomWidth: 200,
  maxRoomWidth: 5000,
  minRoomHeight: 200,
  maxRoomHeight: 5000,
  minTableDiameter: 40,
  maxTableDiameter: 1000,
} as const

export function normalizeRoomConfig(room?: Partial<SeatingRoomConfig> | null): SeatingRoomConfig {
  const clamp = (value: number | null | undefined, min: number, max: number, fallback: number) => {
    if (value == null || Number.isNaN(value)) return fallback
    return Math.min(max, Math.max(min, Math.round(value)))
  }
  return {
    roomWidth: clamp(
      room?.roomWidth,
      ROOM_LIMITS.minRoomWidth,
      ROOM_LIMITS.maxRoomWidth,
      DEFAULT_ROOM_CONFIG.roomWidth
    ),
    roomHeight: clamp(
      room?.roomHeight,
      ROOM_LIMITS.minRoomHeight,
      ROOM_LIMITS.maxRoomHeight,
      DEFAULT_ROOM_CONFIG.roomHeight
    ),
    tableDiameter: clamp(
      room?.tableDiameter,
      ROOM_LIMITS.minTableDiameter,
      ROOM_LIMITS.maxTableDiameter,
      DEFAULT_ROOM_CONFIG.tableDiameter
    ),
  }
}

export const COUPLE_MEMBER_IDS = {
  jan: 'couple-jan',
  michael: 'couple-michael',
} as const

export type CouplePerson = 'jan' | 'michael'

export interface SeatingGroup {
  id: string
  displayName: string
  personCount: number
  isCoupleMember?: boolean
  isTestGuest?: boolean
  additionalLabel?: string | null
  guest?: Guest
  testGuest?: SeatingTestGuest
}

export function getCoupleSeatingGroups(): SeatingGroup[] {
  return [
    {
      id: COUPLE_MEMBER_IDS.jan,
      displayName: 'Jan',
      personCount: 1,
      isCoupleMember: true,
    },
    {
      id: COUPLE_MEMBER_IDS.michael,
      displayName: 'Michael',
      personCount: 1,
      isCoupleMember: true,
    },
  ]
}

export function guestToSeatingGroup(guest: Guest): SeatingGroup {
  const additional = guest.additional_guests ?? []
  const additionalLabel = formatAdditionalLabel(additional)

  return {
    id: guest.id,
    displayName: guest.primary_name,
    personCount: getGroupPersonCount(guest),
    additionalLabel,
    guest,
  }
}

function formatAdditionalLabel(
  additional: { name: string; type: string; age?: number | null }[]
): string | null {
  if (additional.length === 0) return null
  return additional
    .map((ag) => {
      const suffix = ag.type === 'child' && ag.age != null ? ` (${ag.age})` : ''
      return `${ag.name}${suffix}`
    })
    .join(', ')
}

export function testGuestToSeatingGroup(testGuest: SeatingTestGuest): SeatingGroup {
  const additional = testGuest.additional_guests ?? []
  const additionalLabel = formatAdditionalLabel(additional)

  return {
    id: testGuest.id,
    displayName: testGuest.primary_name,
    personCount: 1 + additional.length,
    additionalLabel,
    isTestGuest: true,
    testGuest,
  }
}

export function getAllSeatingGroups(
  attendingGuests: Guest[],
  testGuests: SeatingTestGuest[] = []
): SeatingGroup[] {
  return [
    ...getCoupleSeatingGroups(),
    ...attendingGuests.map(guestToSeatingGroup),
    ...testGuests.map(testGuestToSeatingGroup),
  ]
}

export function isCoupleMemberId(id: string): boolean {
  return id === COUPLE_MEMBER_IDS.jan || id === COUPLE_MEMBER_IDS.michael
}

export function coupleIdToPerson(id: string): CouplePerson | null {
  if (id === COUPLE_MEMBER_IDS.jan) return 'jan'
  if (id === COUPLE_MEMBER_IDS.michael) return 'michael'
  return null
}

export function personToCoupleId(person: CouplePerson): string {
  return COUPLE_MEMBER_IDS[person]
}

export function getGroupPersonCount(guest: Guest): number {
  return 1 + (guest.additional_guests?.length ?? 0)
}

export function getAttendingGuests(guests: Guest[]): Guest[] {
  return guests.filter((g) => g.rsvp_status === 'coming')
}

export function sanitizeSeatingAssignments(
  assignments: Record<string, string | null>,
  validGroupIds: Set<string>
): Record<string, string | null> {
  const sanitized: Record<string, string | null> = {}
  for (const [groupId, tableId] of Object.entries(assignments)) {
    if (validGroupIds.has(groupId)) {
      sanitized[groupId] = tableId
    }
  }
  return sanitized
}

export function sanitizeSeatingState(state: SeatingState, attendingGuests: Guest[]): SeatingState {
  const validGroupIds = new Set(
    getAllSeatingGroups(attendingGuests, state.testGuests).map((g) => g.id)
  )
  return {
    configurationId: state.configurationId,
    tables: state.tables.map((t) => ({ ...t })),
    assignments: sanitizeSeatingAssignments(state.assignments, validGroupIds),
    testGuests: state.testGuests.map((tg) => ({
      ...tg,
      additional_guests: tg.additional_guests?.map((ag) => ({ ...ag })),
    })),
    room: normalizeRoomConfig(state.room),
  }
}

export function splitAssignments(
  assignments: Record<string, string | null>,
  testGuestIds: Set<string> = new Set()
) {
  const guestAssignments: Record<string, string | null> = {}
  const coupleAssignments: Partial<Record<CouplePerson, string | null>> = {}

  for (const [id, tableId] of Object.entries(assignments)) {
    if (testGuestIds.has(id)) continue
    const person = coupleIdToPerson(id)
    if (person) {
      coupleAssignments[person] = tableId
    } else {
      guestAssignments[id] = tableId
    }
  }

  return { guestAssignments, coupleAssignments }
}

export function getTablePersonCount(
  tableId: string,
  groups: SeatingGroup[],
  assignments: Record<string, string | null>
): number {
  return groups.reduce((sum, group) => {
    if (assignments[group.id] === tableId) {
      return sum + group.personCount
    }
    return sum
  }, 0)
}

export function getUnassignedGroups(
  groups: SeatingGroup[],
  assignments: Record<string, string | null>
): SeatingGroup[] {
  return groups.filter((g) => !assignments[g.id])
}

export function getGroupsForTable(
  tableId: string,
  groups: SeatingGroup[],
  assignments: Record<string, string | null>
): SeatingGroup[] {
  return groups.filter((g) => assignments[g.id] === tableId)
}

/** Individual guest names assigned to a table, in display order. */
export function getTableGuestNames(
  tableId: string,
  groups: SeatingGroup[],
  assignments: Record<string, string | null>
): string[] {
  const names: string[] = []

  for (const group of getGroupsForTable(tableId, groups, assignments)) {
    if (group.isCoupleMember) {
      names.push(group.displayName)
      continue
    }

    if (group.guest) {
      names.push(group.guest.primary_name)
      for (const additionalGuest of group.guest.additional_guests ?? []) {
        names.push(additionalGuest.name)
      }
      continue
    }

    if (group.testGuest) {
      names.push(group.testGuest.primary_name)
      for (const additionalGuest of group.testGuest.additional_guests ?? []) {
        names.push(additionalGuest.name)
      }
      continue
    }

    names.push(group.displayName)
  }

  return names
}

export function createDefaultTable(index: number, configurationId: string): SeatingTable {
  return {
    id: crypto.randomUUID(),
    configuration_id: configurationId,
    name: `Tisch ${index}`,
    sort_order: index - 1,
    created_at: new Date().toISOString(),
    position_x: null,
    position_y: null,
  }
}

/**
 * Assigns grid positions to tables that have not been placed on the room plan
 * yet (position_x/position_y === null). Tables already placed keep their
 * position. Grid slots are stable per table index so dragging one table does
 * not shift the default positions of others.
 */
export function withPlacedTables(tables: SeatingTable[], room: SeatingRoomConfig): SeatingTable[] {
  const radius = room.tableDiameter / 2
  const margin = radius + Math.max(20, room.tableDiameter * 0.25)
  const usableWidth = Math.max(1, room.roomWidth - margin * 2)
  const step = Math.max(room.tableDiameter * 1.4, 1)
  const perRow = Math.max(1, Math.floor(usableWidth / step) + 1)

  return tables.map((table, index) => {
    if (table.position_x != null && table.position_y != null) {
      return table
    }
    const col = index % perRow
    const rowIdx = Math.floor(index / perRow)
    const x = clampPosition(margin + col * step, radius, room.roomWidth)
    const y = clampPosition(margin + rowIdx * step, radius, room.roomHeight)
    return { ...table, position_x: x, position_y: y }
  })
}

/** Writes default grid positions into tables that are still unplaced. */
export function ensureTablePositions(tables: SeatingTable[], room: SeatingRoomConfig): SeatingTable[] {
  return withPlacedTables(tables, room).map((placed, index) => {
    const original = tables[index]!
    if (original.position_x != null && original.position_y != null) {
      return original
    }
    return {
      ...original,
      position_x: placed.position_x,
      position_y: placed.position_y,
    }
  })
}

/** Clamps a table center coordinate so the table stays fully inside the hall. */
export function clampPosition(value: number, radius: number, extent: number): number {
  const min = radius
  const max = Math.max(radius, extent - radius)
  return Math.min(max, Math.max(min, value))
}

export function autoDistributeGroups(
  groups: SeatingGroup[],
  tables: SeatingTable[]
): Record<string, string | null> {
  if (tables.length === 0) return {}

  const newAssignments: Record<string, string | null> = {}
  const tableCounts = new Map<string, number>()

  for (const table of tables) {
    tableCounts.set(table.id, 0)
  }

  const sortedGroups = [...groups].sort((a, b) => b.personCount - a.personCount)

  for (const group of sortedGroups) {
    let minTable = tables[0]!
    let minCount = tableCounts.get(minTable.id) ?? 0

    for (const table of tables) {
      const count = tableCounts.get(table.id) ?? 0
      if (count < minCount) {
        minCount = count
        minTable = table
      }
    }

    newAssignments[group.id] = minTable.id
    tableCounts.set(minTable.id, minCount + group.personCount)
  }

  return newAssignments
}

export function cloneSeatingState(state: SeatingState): SeatingState {
  return {
    configurationId: state.configurationId,
    tables: state.tables.map((t) => ({ ...t })),
    assignments: { ...state.assignments },
    testGuests: state.testGuests.map((tg) => ({
      ...tg,
      additional_guests: tg.additional_guests?.map((ag) => ({ ...ag })),
    })),
    room: { ...state.room },
  }
}

export function createTestGuestDraft(
  primaryName: string,
  additionalGuests: SeatingTestGuestDraft['additional_guests'] = []
): SeatingTestGuestDraft {
  return {
    id: crypto.randomUUID(),
    primary_name: primaryName.trim(),
    additional_guests: additionalGuests.map((ag) => ({ ...ag, id: ag.id || crypto.randomUUID() })),
  }
}

export function testGuestDraftToSeatingTestGuest(
  draft: SeatingTestGuestDraft,
  configurationId: string
): SeatingTestGuest {
  return {
    id: draft.id,
    configuration_id: configurationId,
    primary_name: draft.primary_name,
    table_id: null,
    created_at: new Date().toISOString(),
    additional_guests: draft.additional_guests.map((ag, index) => ({
      id: ag.id,
      test_guest_id: draft.id,
      name: ag.name,
      type: ag.type,
      age: ag.age ?? null,
      sort_order: index,
    })),
  }
}

export function getGroupLabel(group: SeatingGroup): string {
  if (group.additionalLabel) {
    return `${group.displayName} + ${group.additionalLabel}`
  }
  return group.displayName
}
